import { throwErrorMessage } from 'dashboard/store/utils/api';
import ConversationApi from '../../../../api/inbox/conversation';
import mutationTypes from '../../../mutation-types';

// Eltafouk: `markMessagesRead` defers its store write by 4 seconds (see below),
// and that timer used to be un-cancellable. Opening a conversation and then
// marking it unread from the card menu within those 4 seconds let the stale
// read-timer land *after* the unread commit and silently zero the badge the
// agent had just set — the row went back to "read" on its own with no request
// and no refresh, while the database stayed unread. Keeping the handle per
// conversation id lets `markMessagesUnread` cancel a still-pending write.
// Timers are re-armed on every conversation update for the open chat, so more
// than one can be in flight; the map is keyed by id and always holds the latest.
const pendingReadTimers = new Map();

const cancelPendingReadTimer = id => {
  const timer = pendingReadTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    pendingReadTimers.delete(id);
  }
};

// Cancelling the timer is not enough on its own: the timer is only armed once
// the read request RESOLVES, so a mark-as-unread issued while that request is
// still in flight finds nothing to cancel and the read then arms a fresh timer
// that undoes it 4s later — the same bug by the reverse ordering. This tick
// orders the two operations: a read whose request started before the last
// mark-as-unread for that conversation does not get to schedule its write.
let readOrderTick = 0;
const lastUnreadTick = new Map();

export default {
  markMessagesRead: async ({ commit, dispatch }, data) => {
    readOrderTick += 1;
    const startedAt = readOrderTick;
    try {
      const {
        data: {
          id,
          agent_last_seen_at: lastSeen,
          unread_count: unreadCount = 0,
        },
      } = await ConversationApi.markMessageRead(data);
      // Eltafouk: commit the count the server actually reports instead of
      // letting the mutation's `unreadCount = 0` default decide. Comment
      // conversations deliberately refuse the read stamp
      // (ConversationsController#update_last_seen keeps them in «محتاج-رد»
      // until genuinely handled) and answer 200 with the UNCHANGED, still
      // unread conversation — assuming 0 made the UI show a read that never
      // happened, and the badge came straight back on the next fetch. On DM
      // inboxes the server really did write the stamp, so it returns 0 here
      // and the behaviour is unchanged.
      //
      // Skip scheduling entirely when a mark-as-unread was issued after this
      // request left: that action already committed the authoritative state
      // and the server row is unread, so this stale response must not schedule
      // a write over it. The mention refresh below still runs — the server
      // cleared the mention regardless of the read stamp.
      if ((lastUnreadTick.get(id) || 0) < startedAt) {
        cancelPendingReadTimer(id);
        pendingReadTimers.set(
          id,
          setTimeout(() => {
            pendingReadTimers.delete(id);
            commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
              id,
              lastSeen,
              unreadCount,
            });
          }, 4000)
        );
      }
      // Opening the conversation clears its unread mention server-side;
      // refetch only the mention badge so the sidebar updates without
      // overwriting the open list's tab counters with whole-inbox totals.
      dispatch('conversationStats/getMentionCount', null, { root: true });
      // Also drop it from the unread-mention list right away so the row marker
      // and the Mentions unread filter update without needing a refresh.
      dispatch('conversationStats/markMentionRead', id, { root: true });
      // Eltafouk: hand the server's real count back to the caller. Several
      // paths answer 200 WITHOUT changing anything — comment conversations
      // refuse the read stamp entirely — so "the dispatch didn't throw" is not
      // proof the row flipped, and a caller adjusting a counter on that
      // assumption would drift.
      return unreadCount;
    } catch (error) {
      // Handle error
    }
    return undefined;
  },

  markMessagesUnread: async ({ commit }, { id }) => {
    // Claim the ordering tick and kill any already-armed timer up front, so a
    // read that RESOLVED before this point cannot fire, and one still IN FLIGHT
    // sees a newer tick when it returns and declines to schedule at all.
    readOrderTick += 1;
    lastUnreadTick.set(id, readOrderTick);
    cancelPendingReadTimer(id);
    try {
      const {
        data: { agent_last_seen_at: lastSeen, unread_count: unreadCount },
      } = await ConversationApi.markMessagesUnread({ id });
      // Again once the response lands: a websocket update for the open chat
      // can re-arm the read path during the round-trip.
      cancelPendingReadTimer(id);
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
        id,
        lastSeen,
        unreadCount,
      });
      // See markMessagesRead: #unread also answers 200 without changing
      // anything when the conversation has no incoming message to be unread
      // about, so the caller needs the real number, not just "no exception".
      return unreadCount;
    } catch (error) {
      throwErrorMessage(error);
    }
    return undefined;
  },
};
